import { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { CUSTOMER_TYPE } from '../constants/customerType';
import { useAuth } from './AuthContext';

const CustomerSelectorContext = createContext({});

export function CustomerSelectorProvider({ children }) {
    const { user } = useAuth();
    const location = useLocation();
    const [selectedCustomerId, setSelectedCustomerId] = useState(null);
    const [selectedCustomerName, setSelectedCustomerName] = useState('');
    const [selectedCustomerType, setSelectedCustomerType] = useState(null);
    const [isManagingOwnData, setIsManagingOwnData] = useState(true);
    // Empresa de transporte deve sempre ver o seletor até escolher; não usar localStorage para estado inicial.
    const [isSelectorOpen, setIsSelectorOpen] = useState(true);

    // Ref para rastrear a rota anterior e limpar seleção apenas na navegação
    const prevPathRef = useRef(location.pathname);
    const hasAppliedInitialSelectionRef = useRef(false);

    const userRoles = user?.roles || [];
    // isTransportCompany: vem do login (userData.isTransportCompany); fallback para role se indefinido
    const isTransportCompany =
        user?.isTransportCompany === true ||
        (user?.isTransportCompany === undefined && userRoles.includes('ROLE_TRANSPORT_COMPANY'));

    // Função para limpar a seleção (apenas para empresas de transporte)
    const clearSelection = useCallback(() => {
        if (!isTransportCompany) return;

        setIsManagingOwnData(true);
        setSelectedCustomerId(null);
        setSelectedCustomerName('');
        setSelectedCustomerType(null);
        setIsSelectorOpen(true); // Abre o seletor ao limpar
        localStorage.removeItem('selectedCustomerId');
        localStorage.removeItem('selectedCustomerName');
        localStorage.removeItem('selectedCustomerType');
        localStorage.removeItem('isManagingOwnData');
    }, [isTransportCompany]);

    // Limpa seleção apenas ao sair da área de entregas (evita limpar ao cancelar nova entrega)
    useEffect(() => {
        const prev = prevPathRef.current;
        const current = location.pathname;
        if (prev === current) return;
        prevPathRef.current = current;
        const wasInEntregas = prev.startsWith('/entregas');
        const isInEntregas = current.startsWith('/entregas');
        if (wasInEntregas && !isInEntregas && isTransportCompany) {
            clearSelection();
        }
    }, [location.pathname, isTransportCompany, clearSelection]);

    // Login / logout: aplica seleção inicial ou permite re-aplicar após novo login
    useEffect(() => {
        if (!user) {
            hasAppliedInitialSelectionRef.current = false;
            return;
        }
        if (hasAppliedInitialSelectionRef.current) return;
        hasAppliedInitialSelectionRef.current = true;

        if (!isTransportCompany) {
            const ownId = user?.customerId ?? user?.idCustomer ?? user?.idEmpresa ?? user?.id ?? null;
            if (ownId != null) {
                setSelectedCustomerId(String(ownId));
                setSelectedCustomerName('Minha Empresa');
                setSelectedCustomerType(null);
                setIsManagingOwnData(true);
                setIsSelectorOpen(false);
            }
            return;
        }

        // Empresa de transporte: não restaura seleção; o usuário deve escolher explicitamente.
        setIsSelectorOpen(true);
    }, [user, isTransportCompany]);

    // Função para selecionar dados próprios
    const selectOwnData = (customerId = null) => {
        const idToUse = customerId || user?.idCustomer || user?.idEmpresa || user?.customerId || user?.id || null;
        setIsManagingOwnData(true);
        setSelectedCustomerId(idToUse);
        setSelectedCustomerName('Minha Empresa');
        setSelectedCustomerType(null);
        setIsSelectorOpen(false); // Fecha o seletor
        localStorage.setItem('isManagingOwnData', 'true');
        localStorage.setItem('selectedCustomerId', idToUse || '');
        localStorage.setItem('selectedCustomerName', 'Minha Empresa');
        localStorage.removeItem('selectedCustomerType');
    };

    // Função para selecionar dados de um cliente
    const selectCustomerData = (customerId, customerName = '', customerType = null) => {
        setIsManagingOwnData(false);
        setSelectedCustomerId(customerId);
        setSelectedCustomerName(customerName);
        const resolvedType =
            customerType === CUSTOMER_TYPE.PESSOA_FISICA || customerType === CUSTOMER_TYPE.PESSOA_JURIDICA
                ? customerType
                : customerId
                  ? CUSTOMER_TYPE.PESSOA_JURIDICA
                  : null;
        setSelectedCustomerType(resolvedType);

        if (customerId) {
            setIsSelectorOpen(false); // Fecha o seletor apenas se selecionou um cliente
        } else {
            setIsSelectorOpen(true); // Mantém aberto se estiver apenas trocando de aba
        }

        localStorage.setItem('isManagingOwnData', 'false');
        localStorage.setItem('selectedCustomerId', customerId ?? '');
        localStorage.setItem('selectedCustomerName', customerName);
        if (resolvedType) {
            localStorage.setItem('selectedCustomerType', resolvedType);
        } else {
            localStorage.removeItem('selectedCustomerType');
        }
    };

    return (
        <CustomerSelectorContext.Provider
            value={{
                selectedCustomerId,
                selectedCustomerName,
                selectedCustomerType,
                isManagingOwnData,
                isTransportCompany,
                isSelectorOpen,
                setIsSelectorOpen,
                selectOwnData,
                selectCustomerData,
                clearSelection,
            }}
        >
            {children}
        </CustomerSelectorContext.Provider>
    );
}

export function useCustomerSelector() {
    return useContext(CustomerSelectorContext);
}
